import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import { useUser } from './contexts/useUser';
import InstallPrompt from './components/InstallPrompt';

// Lazy-load auth contexts to prevent Firebase from loading on initial page load
const AuthenticatedApp = React.lazy(() => import('./components/AuthenticatedApp'));
import { GuestUserProvider } from './contexts/GuestUserProvider';
import BottomNav from './components/BottomNav';
import UpdatePrompt from './components/UpdatePrompt';
const InfoModal = React.lazy(() => import('./components/InfoModal'));
import DesktopFooter from './layouts/DesktopFooter';
import OfflineBanner from './components/OfflineBanner';
import InAppNotification from './components/InAppNotification';
import AnnouncementNotifier from './components/AnnouncementNotifier';
import useKeyboardInsets from './hooks/useKeyboardInsets';
const ToastContainer = React.lazy(() =>
  Promise.all([
    import('react-toastify'),
    import('react-toastify/dist/ReactToastify.css'),
  ]).then(([m]) => ({ default: m.ToastContainer }))
);
// Lazy-load LogBeanDialog to prevent Firebase from loading on initial page load
const LogBeanDialog = React.lazy(() => import('./components/LogBeanDialog'));
// Lazy-load addBeanLog to prevent Firebase from loading on initial page load
// import { addBeanLog } from './utils/db';
import type { BeanLogInput } from './types/BeanLog';
import BackToTop from './components/BackToTop';
import { SearchProvider } from './contexts/SearchContext';
// useGeolocation and useSearchStore removed — geolocation is now deferred to first search (inside runSearch)
import { ViewModeProvider } from './contexts/ViewModeContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import { NavigationGuardOnce } from 'utils/navGuard';
import DownloadRedirect from './pages/DownloadRedirect';
const Home = React.lazy(() => import('pages/Home'));
const Recipes = React.lazy(() => import('./pages/Recipes'));
const Hounds = React.lazy(() => import('./pages/Hounds'));
const CoffeeShopDetailsPage = React.lazy(() => import('./pages/CoffeeShopDetailsPage'));
const CoffeeBrandDetailsPage = React.lazy(() => import('./pages/CoffeeBrandDetailsPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ResetPasswordConfirm = React.lazy(() => import('./pages/ResetPasswordConfirm'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const ProfessionalProfile = React.lazy(() => import('./pages/ProfessionalProfile'));
const FriendProfile = React.lazy(() => import('./pages/FriendProfile'));
const SearchResults = React.lazy(() => import('./pages/SearchResults'));
const SubmitShop = React.lazy(() => import('./pages/SubmitShop'));
const SubmitRoaster = React.lazy(() => import('./pages/SubmitRoaster'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AdminClaimsPage = React.lazy(() => import('./pages/AdminClaimsPage'));
const MyBusinessesPage = React.lazy(() => import('./pages/MyBusinessesPage'));

// Container components
const PageContainer: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  const { mode } = useTheme();
  const bgColor = mode === 'light' ? 'bg-[#FAF7F2]' : 'bg-[#121212]';
  return (
    <div className={`min-h-screen flex flex-col ${bgColor} relative w-full max-w-full box-border transition-colors duration-300 ${className}`}
      style={{ paddingBottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}>
      {children}
    </div>
  );
};

// Protected Route component
interface ProtectedRouteProps {
  element: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element }) => {
  const { user, loading } = useUser();
  const location = useLocation();
  if (loading) {
    return <div>Loading...</div>;
  }
  return user ? element : (
    <Navigate to="/login" replace state={{ from: location }} />
  );
};

const ConditionalGuestUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
  if (isAuthPage) {
    return <>{children}</>;
  }
  return <GuestUserProvider>{children}</GuestUserProvider>;
};

// RouteLogger removed

// --- MAIN APP COMPONENT ---
const App: React.FC = () => {
  useKeyboardInsets();
  
  // Initialize theme mode from localStorage or system preference
  const [mode, setMode] = React.useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    const userHasManuallySet = localStorage.getItem('themeManuallySet') === 'true';
    
    // If user has manually set theme, use that
    if (userHasManuallySet && (savedMode === 'light' || savedMode === 'dark')) {
      return savedMode;
    }
    
    // Otherwise, use system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });
  
  const [deferredPrompt, setDeferredPrompt] = React.useState<
    BeforeInstallPromptEvent | null
  >(null);
  
  const toggleColorMode = React.useCallback(() => {
    setMode((prev) => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      localStorage.setItem('themeManuallySet', 'true');
      return newMode;
    });
  }, []);


  React.useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const AppInner: React.FC = () => {
    const location = useLocation();
    const { user } = useUser();
    const { mode: themeMode } = useTheme();
    const [infoOpen, setInfoOpen] = React.useState(false);
    const [beanDialogOpen, setBeanDialogOpen] = React.useState(false);
    
    // Sync App's local mode state with ThemeContext
    React.useEffect(() => {
      setMode(themeMode);
    }, [themeMode]);

    // Geolocation is now requested on first search (inside runSearch) to avoid
    // Lighthouse "geolocation on page load" penalty and reduce initial blocking.

    const handleBeanSubmit = async (data: BeanLogInput) => {
      if (!user?.uid) {
        throw new Error('You must be logged in to log beans');
      }
      // Lazy-load addBeanLog to prevent Firebase from loading on initial page load
      const { addBeanLog } = await import('./utils/db');
      await addBeanLog(user.uid, data);
    };

    return (
      <>
        {/* Skip to main content link for keyboard navigation */}
        <a
          href="#main-content"
          className="absolute -left-[9999px] z-[9999] p-4 bg-primary text-white font-bold no-underline focus:left-2.5 focus:top-2.5"
        >
          Skip to main content
        </a>
        <div
          id="app-scroll"
          className={`w-full min-h-screen box-border transition-colors duration-300 ${themeMode === 'light' ? 'bg-[#FAF7F2] text-gray-900' : 'bg-[#121212] text-white'}`}
        >
          <div className="w-full">
            <ViewModeProvider>
              <Header mode={mode} toggleColorMode={toggleColorMode} />
              <React.Suspense fallback={null}>
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  role="status"
                  aria-live="polite"
                />
              </React.Suspense>
              <main id="main-content" tabIndex={-1} key={location.pathname} className="route-fade-in">
                              <NavigationGuardOnce />
                                <Routes location={location}>
                                <Route
                                  path="/login"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <Login />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/signup"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <Signup />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/reset-password"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <ResetPassword />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/reset-password-confirm"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <ResetPasswordConfirm />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <ErrorBoundary>
                                        <PageContainer>
                                          <Home />
                                        </PageContainer>
                                      </ErrorBoundary>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/search"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <SearchResults />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/recipes"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <Recipes />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/hounds"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <Hounds />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route path="/download" element={<DownloadRedirect />} />
                                <Route
                                  path="/submit-shop"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <ErrorBoundary>
                                        <PageContainer>
                                          <SubmitShop />
                                        </PageContainer>
                                      </ErrorBoundary>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/submit-roaster"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <ErrorBoundary>
                                        <PageContainer>
                                          <SubmitRoaster />
                                        </PageContainer>
                                      </ErrorBoundary>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/admin/claims"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <ErrorBoundary>
                                        <PageContainer>
                                          <AdminClaimsPage />
                                        </PageContainer>
                                      </ErrorBoundary>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/my-businesses"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <ErrorBoundary>
                                        <PageContainer>
                                          <MyBusinessesPage />
                                        </PageContainer>
                                      </ErrorBoundary>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/settings"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <ErrorBoundary>
                                        <PageContainer>
                                          <Settings mode={mode} toggleColorMode={toggleColorMode} />
                                        </PageContainer>
                                      </ErrorBoundary>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/profile"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <AuthenticatedApp>
                                        <ProtectedRoute
                                          element={
                                            <PageContainer>
                                              <UserProfile />
                                            </PageContainer>
                                          }
                                        />
                                      </AuthenticatedApp>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/shop/:id"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <CoffeeShopDetailsPage />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/brand/:id"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <CoffeeBrandDetailsPage />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/professional/:id"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <PageContainer>
                                        <ProfessionalProfile />
                                      </PageContainer>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/friend/:id"
                                  element={
                                    <Suspense fallback={<div>Loading…</div>}>
                                      <AuthenticatedApp>
                                        <PageContainer>
                                          <FriendProfile />
                                        </PageContainer>
                                      </AuthenticatedApp>
                                    </Suspense>
                                  }
                                />
                              </Routes>
              </main>
            </ViewModeProvider>
          </div>
        </div>
        <BottomNav 
          onInfo={() => setInfoOpen(true)} 
          onBeanLog={() => setBeanDialogOpen(true)}
        />
        <InstallPrompt
          deferredPrompt={deferredPrompt}
          setDeferredPrompt={setDeferredPrompt}
        />
        <OfflineBanner />
        <UpdatePrompt />
        <DesktopFooter onInfo={() => setInfoOpen(true)} />
        <AnnouncementNotifier />
        <InAppNotification />
        <React.Suspense fallback={null}>
          <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
        </React.Suspense>
        {beanDialogOpen && (
          <React.Suspense fallback={null}>
            <LogBeanDialog
              open={beanDialogOpen}
              onClose={() => setBeanDialogOpen(false)}
              onSubmit={handleBeanSubmit}
            />
          </React.Suspense>
        )}
        <BackToTop />
      </>
    );
  };

  return (
    <ThemeProvider>
      <SearchProvider>
        <BrowserRouter>
          <ConditionalGuestUserProvider>
            <Suspense fallback={<div>Loading...</div>}>
              <AppInner />
            </Suspense>
          </ConditionalGuestUserProvider>
        </BrowserRouter>
      </SearchProvider>
    </ThemeProvider>
  );
};

export default App;
export { ProtectedRoute };
